/**
 * AIPoliciesKillSwitchControl — operator-side per-tenant per-capability halt UI.
 *
 * WQ-232 (Phase 4.9 α6). Appended to AIPoliciesPage as a sibling card to
 * the existing TenantKillSwitchCard (Redis-backed global tenant halt).
 *
 * Renders the toggles for the active tenant context (from
 * useAdminContext().activeTenant). Each row shows current halt state
 * with a halt or restore action. The rendered toggle list comes from
 * the operator endpoint /api/admin/ai/kill-switch/tenants/{tenant}/toggles
 * which iterates CapabilityType.__members__ — frontend never hardcodes
 * the list.
 *
 * Mutation requires AI_KILL_SWITCH permission server-side; this
 * component surfaces 403 errors from the service layer.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminContext } from '../../contexts/AdminContextProvider';
import * as svc from '../../services/policiesKillSwitchService';
import type { HaltToggle } from '../../services/policiesKillSwitchService';

const POLL_MS = 30_000;

type Confirm =
  | { kind: 'halt'; capability: string }
  | { kind: 'restore'; capability: string }
  | { kind: 'halt-all' }
  | { kind: 'restore-all' }
  | null;

const AIPoliciesKillSwitchControl: React.FC = () => {
  const { authFetch } = useAuth();
  const { activeTenant } = useAdminContext();
  const tenantId = activeTenant?.id || null;

  const [toggles, setToggles] = useState<HaltToggle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [reason, setReason] = useState('');
  const handle = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    if (!tenantId) return;
    try {
      setError(null);
      const data = await svc.listTenantToggles(authFetch, tenantId);
      setToggles(data);
    } catch (e) {
      setError((e as Error).message || 'Failed to load halt status');
    } finally {
      setLoading(false);
    }
  }, [authFetch, tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    refresh();
    handle.current = setInterval(refresh, POLL_MS);
    return () => {
      if (handle.current) clearInterval(handle.current);
    };
  }, [tenantId, refresh]);

  const haltedCount = useMemo(
    () => (toggles ? toggles.filter((t) => t.is_halted).length : 0),
    [toggles],
  );

  const open = (c: Confirm) => {
    setReason('');
    setConfirm(c);
  };
  const close = () => {
    setConfirm(null);
    setReason('');
  };

  const performConfirm = async () => {
    if (!confirm || !tenantId) return;
    setPending(true);
    try {
      if (confirm.kind === 'halt') {
        await svc.operatorHalt(authFetch, tenantId, confirm.capability, reason || undefined);
      } else if (confirm.kind === 'restore') {
        await svc.operatorRestore(authFetch, tenantId, confirm.capability, reason || undefined);
      } else if (confirm.kind === 'halt-all') {
        await svc.operatorHaltAll(authFetch, tenantId, reason || undefined);
      } else if (confirm.kind === 'restore-all') {
        await svc.operatorRestoreAll(authFetch, tenantId, reason || undefined);
      }
      close();
      await refresh();
    } catch (e) {
      setError((e as Error).message || 'Action failed');
    } finally {
      setPending(false);
    }
  };

  if (!tenantId) return null;

  return (
    <section
      data-testid="ai-policies-kill-switch-control"
      style={{
        padding: 20, marginBottom: 24, borderRadius: 8,
        border: '1px solid var(--border)', background: 'var(--bg-card)',
      }}
    >
      <header style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>Per-Capability Halt — Operator Matrix</h3>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 12 }}>
            Halt or restore individual capabilities for {activeTenant?.name || 'this tenant'}.
            Distinct from the global tenant kill switch above.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            data-testid="op-halt-all-btn"
            type="button"
            disabled={pending || !toggles}
            onClick={() => open({ kind: 'halt-all' })}
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 12,
              background: 'var(--danger, #c0392b)', color: '#fff', border: 'none',
            }}
          >
            Halt All
          </button>
          <button
            data-testid="op-restore-all-btn"
            type="button"
            disabled={pending || !toggles || haltedCount === 0}
            onClick={() => open({ kind: 'restore-all' })}
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 12,
              background: 'var(--success, #27ae60)', color: '#fff', border: 'none',
              opacity: !toggles || haltedCount === 0 ? 0.5 : 1,
            }}
          >
            Restore All
          </button>
        </div>
      </header>

      {error && (
        <div role="alert" data-testid="op-halt-error"
          style={{ padding: 8, marginBottom: 8, color: 'var(--danger, #c0392b)' }}
        >
          {error}
        </div>
      )}

      {loading && <p data-testid="op-halt-loading">Loading capability halts…</p>}

      {!loading && toggles && (
        <table
          data-testid="op-halt-table"
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid var(--border)' }}>Capability</th>
              <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid var(--border)' }}>Status</th>
              <th style={{ textAlign: 'right', padding: 6, borderBottom: '1px solid var(--border)' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {toggles.map((t) => (
              <tr key={t.capability_type} data-testid={`op-halt-row-${t.capability_type}`}>
                <td style={{ padding: 6, textTransform: 'capitalize' }}>
                  {t.capability_type.replace(/_/g, ' ')}
                </td>
                <td style={{ padding: 6 }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                    fontSize: 11, fontWeight: 600,
                    background: t.is_halted ? 'rgba(192, 57, 43, 0.15)' : 'rgba(39, 174, 96, 0.15)',
                    color: t.is_halted ? 'var(--danger, #c0392b)' : 'var(--success, #27ae60)',
                  }}>
                    {t.is_halted ? 'HALTED' : 'Active'}
                  </span>
                </td>
                <td style={{ padding: 6, textAlign: 'right' }}>
                  {t.is_halted ? (
                    <button
                      type="button"
                      data-testid={`op-restore-btn-${t.capability_type}`}
                      disabled={pending}
                      onClick={() => open({ kind: 'restore', capability: t.capability_type })}
                      style={{
                        padding: '3px 10px', fontSize: 11, borderRadius: 4,
                        border: '1px solid var(--success, #27ae60)',
                        color: 'var(--success, #27ae60)', background: 'transparent',
                      }}
                    >
                      Restore
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid={`op-halt-btn-${t.capability_type}`}
                      disabled={pending}
                      onClick={() => open({ kind: 'halt', capability: t.capability_type })}
                      style={{
                        padding: '3px 10px', fontSize: 11, borderRadius: 4,
                        border: '1px solid var(--danger, #c0392b)',
                        color: 'var(--danger, #c0392b)', background: 'transparent',
                      }}
                    >
                      Halt
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirm && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="op-halt-confirm-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={close}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)', padding: 24, borderRadius: 8,
              maxWidth: 480, width: '90%',
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {confirm.kind === 'halt' && `Halt ${confirm.capability} for tenant?`}
              {confirm.kind === 'restore' && `Restore ${confirm.capability} for tenant?`}
              {confirm.kind === 'halt-all' && 'Halt all capabilities for tenant?'}
              {confirm.kind === 'restore-all' && 'Restore all capabilities for tenant?'}
            </h3>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ fontSize: 13 }}>Reason (optional)</span>
              <textarea
                data-testid="op-halt-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                style={{ width: '100%', marginTop: 4, padding: 6 }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" data-testid="op-halt-cancel-btn" onClick={close} disabled={pending}
                style={{ padding: '6px 14px', borderRadius: 4 }}>
                Cancel
              </button>
              <button
                type="button"
                data-testid="op-halt-confirm-btn"
                onClick={performConfirm}
                disabled={pending}
                style={{
                  padding: '6px 14px', borderRadius: 4, color: '#fff',
                  background: confirm.kind.startsWith('halt')
                    ? 'var(--danger, #c0392b)' : 'var(--success, #27ae60)',
                  border: 'none',
                }}
              >
                {pending ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AIPoliciesKillSwitchControl;
